import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { SelectItem } from './types'

interface NativeSelectProps<ItemType> extends Omit<React.HTMLProps<HTMLSelectElement>, 'onChange'> {
  items: ItemType[]
  onChange: (itemIndex: number) => void
}

export function NativeSelect<ItemType extends SelectItem<unknown>>({
  items,
  onChange,
  placeholder = 'Select value',
  ...props
}: NativeSelectProps<ItemType>) {
  const showPlaceholder = props.value === -1
  const handleNativeSelectChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const itemIndex = e.target.selectedIndex + (showPlaceholder ? -1 : 0)
      const selectedItem = items[itemIndex]

      if (!selectedItem.href || !selectedItem.reloadDocument) {
        onChange(itemIndex)
        return
      }

      if (typeof selectedItem.href === 'string') {
        window.location.href = selectedItem.href
        return
      }

      const newUrl = new URL(selectedItem.href.pathname!, window.location.href)
      newUrl.search = selectedItem.href.search ?? ''
      newUrl.hash = selectedItem.href.hash ?? ''

      window.location.href = newUrl.toString()
    },
    [items, onChange, showPlaceholder]
  )

  return (
    <select
      {...props}
      tabIndex={-1}
      className="absolute inset-0 appearance-none opacity-0"
      onChange={handleNativeSelectChange}
    >
      {showPlaceholder && <option hidden>{placeholder}</option>}
      {items.map((item, i) => (
        <option key={i} value={i}>
          {item.label}
        </option>
      ))}
    </select>
  )
}

export function NativeSelectInRouterContext<ItemType extends SelectItem<unknown>>(props: NativeSelectProps<ItemType>) {
  const navigate = useNavigate()

  const onChangeWithNavigate = React.useCallback(
    (itemIndex: number) => {
      const selectedItem = props.items[itemIndex]
      props.onChange(itemIndex)

      if (selectedItem.href) navigate(selectedItem.href)
    },
    // We don’t need the whole props here, just onChange and items
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, props.onChange, props.items]
  )

  return <NativeSelect {...props} onChange={onChangeWithNavigate} />
}
